/**
 * Atendimento (WhatsApp) — caixa de entrada em três painéis, no modelo do Chatwoot
 * (redesign admin): lista de conversas à esquerda, conversa e caixa de resposta no
 * centro, dados do contato à direita. É a tela de `/inbox` e `/inbox/:conversationId`.
 *
 * Os mesmos contratos de antes: conversas do `ConversationCore` pelo dataset do
 * dashboard, conversa aberta com polling (`useChannelConversation`), assumir/devolver
 * e responder pelo WAPI (`useChannelConversationActions`). `/inbox?kpi=...` continua
 * abrindo o drill-down de indicador (`pages/Inbox.tsx`), que é uma lista auditável.
 *
 * Grupos do WhatsApp entram na MESMA estrutura (lista, chat, composer), sinalizados com o selo
 * "Grupo" e filtráveis na aba "Grupos". O WAPI só expõe listar e enviar para grupo — não há
 * histórico recebido —, e a tela declara isso em vez de mostrar uma conversa vazia como se fosse real.
 *
 * Tags de conversa (CRM) aparecem na lista e no topo do chat e são editadas ali mesmo.
 *
 * Os painéis têm rolagem própria de propósito (lista e mensagens longas) — é o padrão
 * de caixa de entrada; a PÁGINA em si não rola.
 */
// Import explícito de `React` — ver o mesmo comentário em `layout/AppShell.tsx`.
import React, { useEffect, useRef, useState, type ReactElement } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowDownUp, ChevronRight, Flame, MessagesSquare, Search, Sparkles, UserCheck, Users } from "lucide-react";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { Inbox } from "./Inbox.js";
import { classifySlaStatus, isSourceAwaitingContract, slaWaitingMinutes, type DashboardDataset } from "../dashboard/kpis.js";
import { formatCount } from "../format.js";
import { useAiStatus, useChannelConversation, useChannelConversationActions, useChatActions, useChatOperators, useConversationTags, useInboxPulse, useInboxTriage, useInboxStats, useQuickReplies, useWhatsAppGroupSend, useWhatsAppGroups } from "../queries.js";
import type { PlatformConversation, WhatsAppGroup } from "../api/types.js";
import type { ChatOperator, ChatOperators } from "../api/ai-types.js";
import type { InboxStats } from "../../../../packages/integration-wapi/src/stats.js";
import { Avatar, Badge, Button, buttonClass, controlClass } from "../ui/index.js";
import { ContactDrawer } from "../components/ContactDrawer.js";
import { conversationExportFilename, conversationToText, downloadTextFile } from "../export/conversationExport.js";
import { toConversationDetail } from "../dashboard/customer.js";
import { ChatCockpit } from "../components/ChatCockpit.js";
import { TagChips, TagEditor } from "../components/ConversationTags.js";
import { ChatAvatar } from "../components/chat/ChatAvatar.js";
import { ChatTimeline } from "../components/chat/ChatTimeline.js";
import { CopilotPanel } from "../components/chat/CopilotPanel.js";
import { GroupPanel } from "../components/chat/GroupPanel.js";
import { conversationPreview, stripJid } from "../components/chat/helpers.js";
import { AiSuggestionDrawer } from "../components/chat/AiSuggestionDrawer.js";
import { aiAnswers, aiLiveMode, type AiLiveMode } from "../components/chat/aiMode.js";
import { attentionOf, firstName, formatWait, lastSenderOf, SORT_LABELS, sortConversations, type SortMode, type TriageNote } from "../components/chat/attention.js";
import { InboxAlerts } from "../components/chat/InboxAlerts.js";

type Filter = "todas" | "aguardando" | "ia" | "grupos";

const NO_TAGS: readonly string[] = [];

/** Mapa id → tags; `null` = rota de tags não publicada. */
export type TagMap = Record<string, string[]> | null;

/** Hora do dia se for hoje; senão dia/mês. Entrada inválida vira "—". */
function shortTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function lastMessage(conversation: PlatformConversation): PlatformConversation["messages"][number] | undefined {
  return conversation.messages.at(-1);
}

/** Aguardando = a última palavra é do cliente e a conversa está com (ou na fila de) humano. */
function isWaiting(conversation: PlatformConversation): boolean {
  // `lastSenderOf`: a lista do WhatsApp não traz mensagens, o servidor informa quem falou por último.
  return (conversation.queuedForHuman || conversation.mode === "human") && lastSenderOf(conversation) === "customer";
}

export function filterConversations(conversations: PlatformConversation[], filter: Filter, query: string, tags: TagMap = null): PlatformConversation[] {
  const needle = query.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  return conversations
    .filter((conversation) => {
      if (filter === "aguardando" && !isWaiting(conversation)) return false;
      if (filter === "ia" && conversation.mode !== "ai") return false;
      if (filter === "grupos") return false;
      if (!needle) return true;
      const haystack = [conversation.displayName, conversation.phone ?? "", lastMessage(conversation)?.text ?? "", ...(tags?.[conversation.id] ?? [])].join(" ");
      return haystack.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().includes(needle);
    })
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export interface AtendimentoViewProps {
  dataset: DashboardDataset;
  selectedId?: string | null;
  /** Conversa ao vivo (polling) da selecionada; substitui a do snapshot. */
  liveConversation?: PlatformConversation;
  /** Slot da caixa de resposta/ações da conversa aberta (injetado pela página). */
  composer?: (conversation: PlatformConversation) => React.ReactNode;
  initialFilter?: Filter;
  now?: Date;
  /** Grupos do WhatsApp (WAPI). `undefined` = ainda não carregados/sem fonte: a aba some, nada é inventado. */
  groups?: WhatsAppGroup[];
  selectedGroupId?: string | null;
  /** Tags por conversa/grupo; `null` = rota ausente (editor declara "aguardando contrato"). */
  tags?: TagMap;
  /** Slot do composer do grupo aberto (injetado pela página, como o das conversas). */
  groupComposer?: (group: WhatsAppGroup) => React.ReactNode;
  /** Reagir a uma mensagem e marcar como lida: injetados pela página (a view não fala com a rede). */
  onReact?: (conversationId: string, messageId: string, emoji: string) => void;
  onMarkRead?: (conversationId: string) => void;
  /** Responder a uma mensagem específica do grupo (a equipe vê quem falou e cita a mensagem); injetado pela página. */
  onReply?: (message: ReplyTarget) => void;
  /** Transcrever um áudio recebido (devolve o texto); injetado pela página, a view não fala com a rede. */
  onTranscribe?: (conversationId: string, messageId: string) => Promise<string>;
  /** Slot do copiloto de IA da conversa aberta (precisa de cliente HTTP, então vem da página). */
  copilot?: (conversation: PlatformConversation) => React.ReactNode;
  /**
   * Estado do assistente de IA. Só `pilot` faz a IA responder sozinha: desligada, em sombra ou em
   * sugestão o atendimento é 100% humano e a tela não diz "a IA está respondendo". `undefined`
   * mantém o comportamento legado (vale o modo da conversa).
   */
  aiMode?: AiLiveMode;
  /** Slot do botão/drawer de sugestão da IA (sombra e sugestão), no topo da conversa aberta. */
  suggestion?: (conversation: PlatformConversation) => React.ReactNode;
  /** Operadores e "quem sou eu" para o selo de responsável e a transferência; `undefined` = recurso indisponível (nada é inventado). */
  operators?: ChatOperators;
  /** Transferir a conversa (ou liberá-la com `null`); injetado pela página, a view não fala com a rede. */
  onAssign?: (conversationId: string, assigneeId: string | null) => void;
  /** Estatísticas gerais de tempo de resposta; `null`/`undefined` = ainda sem dado (a faixa some). */
  stats?: InboxStats | null;
  /** Foto estável do contato: a página guarda a última foto vista, então a lista nunca fica atrás do histórico. */
  pictureFor?: (conversation: PlatformConversation) => string | null;
  /** Slot do alerta de mensagem nova (som + aviso no topo); injetado pela página, precisa de polling. */
  alerts?: React.ReactNode;
  initialSort?: SortMode;
  /** Notas da triagem por IA (id → nota). Vazio/ausente = ainda não triado. */
  triage?: ReadonlyMap<string, TriageNote>;
  /** Pede a triagem por IA; ausente = recurso indisponível e o botão some. */
  onTriage?: () => void;
  triaging?: boolean;
  triageError?: string | null;
}

const defaultPictureFor = (conversation: PlatformConversation): string | null => conversation.contact?.pictureUrl ?? conversation.group?.pictureUrl ?? null;

export function AtendimentoView({ dataset, selectedId = null, liveConversation, composer, initialFilter = "todas", now, groups, selectedGroupId = null, tags = null, groupComposer, onReact, onMarkRead, onTranscribe, onReply, copilot, aiMode, suggestion, operators, onAssign, stats, pictureFor = defaultPictureFor, alerts, initialSort = "recentes", triage, onTriage, triaging = false, triageError = null }: AtendimentoViewProps): ReactElement {
  const reference = now ?? new Date();
  const [filter, setFilter] = useState<Filter>(initialFilter);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortMode>(initialSort);

  if (isSourceAwaitingContract(dataset, "conversations")) {
    return (
      <div className="page page--atendimento flex flex-col gap-6" data-page="atendimento">
        <StatusPanel
          kind="awaiting_source"
          title="Conversas ainda não disponíveis neste ambiente"
          detail="A rota de conversas não respondeu; sem ela não há caixa de entrada para mostrar (exibir vazio diria que ninguém escreveu)."
        />
      </div>
    );
  }

  const all = dataset.conversations;
  const visible = sortConversations(filterConversations(all, filter, query, tags), sort, reference, (conversation) => aiAnswers(aiMode, conversation), triage);
  const groupList = groups ?? [];
  const needle = query.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  const visibleGroups =
    filter === "todas" || filter === "grupos"
      ? groupList
          .filter((group) => !needle || [group.name, ...(tags?.[group.id] ?? [])].join(" ").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().includes(needle))
          .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
      : [];
  const selectedGroup = selectedGroupId ? groupList.find((group) => group.id === selectedGroupId) ?? null : null;
  const selected = selectedGroup ? null : (liveConversation ?? all.find((conversation) => conversation.id === selectedId) ?? null);
  const hasOpen = selected !== null || selectedGroup !== null;
  const waitingCount = all.filter(isWaiting).length;

  return (
    <div
      className="page page--atendimento grid h-[calc(100dvh-10rem)] min-h-[34rem] grid-cols-1 overflow-hidden rounded-panel border border-border bg-surface shadow-s1 lg:grid-cols-[20rem_minmax(0,1fr)] 2xl:grid-cols-[22rem_minmax(0,1fr)_22rem]"
      data-page="atendimento"
      data-conversations={all.length}
      data-groups={groupList.length}
    >
      {/* Painel 1: conversas */}
      <section aria-label="Conversas" className={"min-h-0 flex-col border-r border-border " + (hasOpen ? "hidden lg:flex" : "flex")}>
        <div className="flex flex-col gap-3 border-b border-border p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-xl font-black text-ink">Conversas</h2>
            {alerts}
          </div>
          {stats ? <StatsStrip stats={stats} /> : null}
          <label className="relative block">
            <span className="sr-only">Buscar conversa</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden focusable={false} />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nome, telefone ou mensagem"
              data-filter-control="query"
              className={controlClass + " pl-9"}
            />
          </label>
          <div role="tablist" aria-label="Filtrar conversas" className="flex gap-1 rounded-control bg-surface-soft p-1">
            {(
              [
                ["todas", `Todas ${all.length + groupList.length}`],
                ["aguardando", `Aguardando ${waitingCount}`],
                ...(aiMode === undefined || aiMode === "pilot" ? ([["ia", "IA"]] as Array<[Filter, string]>) : []),
                ...(groups !== undefined ? ([["grupos", `Grupos ${groupList.length}`]] as Array<[Filter, string]>) : [])
              ] as Array<[Filter, string]>
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                onClick={() => setFilter(id)}
                className={
                  "min-h-9 flex-1 whitespace-nowrap rounded-control px-2 text-sm transition-colors " +
                  (filter === id ? "bg-surface font-extrabold text-ink shadow-s1" : "font-bold text-muted hover:text-ink")
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs text-muted">
          <ArrowDownUp className="h-3.5 w-3.5" aria-hidden focusable={false} />
          <label className="flex items-center gap-2">
            <span>Ordenar</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as SortMode)} data-filter-control="sort" className="rounded-control border border-border bg-surface px-2 py-1 text-xs font-bold text-ink">
              {(Object.keys(SORT_LABELS) as SortMode[]).map((mode) => <option key={mode} value={mode}>{SORT_LABELS[mode]}</option>)}
            </select>
          </label>
          {onTriage ? (
            <button
              type="button"
              onClick={() => { onTriage(); setSort("ia"); }}
              disabled={triaging}
              data-action="triage"
              className="ml-auto inline-flex items-center gap-1 rounded-pill border border-border px-2.5 py-1 font-bold text-ink transition-colors hover:bg-surface-soft disabled:opacity-60"
            >
              <Sparkles className="h-3.5 w-3.5 text-brand-text" aria-hidden focusable={false} />
              {triaging ? "Analisando…" : "Priorizar com IA"}
            </button>
          ) : null}
        </div>
        {triageError ? <p className="m-0 border-b border-border px-4 py-2 text-xs text-danger" role="alert" data-triage-error>{triageError}</p> : null}
        <ul className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0" aria-label="Lista de conversas">
          {visible.length === 0 && visibleGroups.length === 0 ? (
            <li className="p-6 text-center text-sm text-muted">Nenhuma conversa neste filtro.</li>
          ) : (
            visible.map((conversation) => {
              const last = lastMessage(conversation);
              const active = conversation.id === selected?.id;
              const waiting = isWaiting(conversation);
              const attention = attentionOf(conversation, reference, aiAnswers(aiMode, conversation));
              return (
                <li key={conversation.id} data-record-id={conversation.id} data-attention={attention.level}>
                  <Link
                    to={`/inbox/${conversation.id}`}
                    aria-current={active ? "true" : undefined}
                    className={
                      "flex items-start gap-3 border-b border-border px-4 py-3 transition-colors hover:bg-surface-soft " +
                      (active ? "bg-brand-wash" : "")
                    }
                  >
                    <ChatAvatar name={stripJid(conversation.displayName || conversation.customerId)} pictureUrl={pictureFor(conversation)} group={conversation.kind === "group"} />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-extrabold text-ink">{stripJid(conversation.displayName || conversation.customerId)}</span>
                        <span className="shrink-0 text-xs text-muted">{last ? shortTime(last.createdAt, reference) : conversation.updatedAt ? shortTime(conversation.updatedAt, reference) : ""}</span>
                      </span>
                      <span className="truncate text-sm text-muted">{stripJid(conversationPreview({ messages: conversation.messages, lastMessagePreview: conversation.lastMessagePreview }))}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        <TagChips tags={tags?.[conversation.id] ?? NO_TAGS} />
                        {conversation.kind === "group" ? <Badge tone="info"><Users className="h-3 w-3" aria-hidden focusable={false} />Grupo</Badge> : null}
                        <Badge tone={conversation.channel === "whatsapp" ? "success" : "neutral"}>{conversation.channel === "whatsapp" ? "WhatsApp" : "Chat"}</Badge>
                        {conversation.unreadCount ? <span className="inline-flex min-w-5 items-center justify-center rounded-pill bg-brand px-1.5 text-xs font-extrabold text-on-brand" data-unread={conversation.unreadCount} aria-label={`${conversation.unreadCount} não lidas`}>{conversation.unreadCount}</span> : null}
                        {conversation.mode === "human" ? <Badge tone="warning">Humano</Badge> : aiAnswers(aiMode, conversation) ? <Badge tone="info">IA</Badge> : null}
                        {triage?.get(conversation.id) ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-text" data-ai-urgency={triage.get(conversation.id)!.urgency} title={triage.get(conversation.id)!.reason}>
                            <Sparkles className="h-3 w-3" aria-hidden focusable={false} />
                            {triage.get(conversation.id)!.urgency} · {triage.get(conversation.id)!.reason}
                          </span>
                        ) : null}
                        {conversation.assignee ? (
                          <Badge tone="neutral"><UserCheck className="h-3 w-3" aria-hidden focusable={false} />{firstName(conversation.assignee.name)}</Badge>
                        ) : conversation.mode === "human" || attention.level !== "none" ? (
                          <span className="text-xs font-bold text-muted" data-owner="none">Sem responsável</span>
                        ) : null}
                        {attention.level === "critical" || attention.level === "high" ? (
                          <span className={"inline-flex items-center gap-1 text-xs font-extrabold " + (attention.level === "critical" ? "text-danger" : "text-warning")} data-attention-reason={attention.reason} title={attention.reason}>
                            <Flame className="h-3 w-3" aria-hidden focusable={false} />
                            Parada há {formatWait((attention.waitingMinutes ?? 0) * 60)}
                          </span>
                        ) : waiting ? (
                          <span className="inline-flex items-center gap-1 text-xs font-extrabold text-danger" data-waiting="true">
                            <span className="h-2 w-2 rounded-pill bg-danger" aria-hidden />
                            Aguardando
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })
          )}
          {visibleGroups.map((group) => {
            const active = group.id === selectedGroup?.id;
            return (
              <li key={group.id} data-record-id={group.id} data-kind="group">
                <Link
                  to={`/inbox/grupo/${encodeURIComponent(group.id)}`}
                  aria-current={active ? "true" : undefined}
                  className={"flex items-start gap-3 border-b border-border px-4 py-3 transition-colors hover:bg-surface-soft " + (active ? "bg-brand-wash" : "")}
                >
                  <GroupAvatar name={group.name} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-extrabold text-ink">{group.name}</span>
                    <span className="truncate text-sm text-muted">{group.size !== null ? `${formatCount(group.size)} participantes` : "Participantes não informados"}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      <TagChips tags={tags?.[group.id] ?? NO_TAGS} />
                      <Badge tone="info"><Users className="h-3 w-3" aria-hidden focusable={false} />Grupo</Badge>
                      {group.simulated ? <Badge tone="warning">Simulador</Badge> : null}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Painel 2: conversa */}
      <section aria-label="Conversa" className={"min-h-0 min-w-0 flex-col " + (hasOpen ? "flex" : "hidden lg:flex")}>
        {selectedGroup ? (
          <GroupPane group={selectedGroup} tags={tags} composer={groupComposer} />
        ) : selected ? (
          <ChatPane conversation={selected} reference={reference} composer={composer} tags={tags} operators={operators} onAssign={onAssign} pictureFor={pictureFor} onReact={onReact} onMarkRead={onMarkRead} onTranscribe={onTranscribe} onReply={onReply} aiMode={aiMode} suggestion={suggestion} />
        ) : (
          <ChatEmpty />
        )}
      </section>

      {/* Painel 3: contato */}
      {selected ? (selected.kind === "group" ? <GroupPanel conversation={selected} /> : <ContactPane conversation={selected} reference={reference} copilot={copilot} aiMode={aiMode} pictureFor={pictureFor} />) : <aside className="hidden border-l border-border 2xl:block" aria-hidden />}
    </div>
  );
}

/** Faixa de estatísticas gerais (últimos 30 dias). Número sem amostra aparece como "—", nunca como 0. */
function StatsStrip({ stats }: { stats: InboxStats }): ReactElement {
  const tile = (label: string, value: string, hint: string, alert = false) => (
    <div className="flex min-w-0 flex-col rounded-control bg-surface-soft px-2.5 py-1.5" title={hint}>
      <span className={"truncate text-sm font-black " + (alert ? "text-danger" : "text-ink")}>{value}</span>
      <span className="truncate text-[11px] font-bold text-muted">{label}</span>
    </div>
  );
  const window = `Últimos ${stats.windowDays} dias · ${formatCount(stats.conversations)} conversas`;
  return (
    <div className="grid grid-cols-3 gap-2" data-section="inbox-stats" aria-label="Estatísticas de atendimento">
      {tile("1ª resposta", formatWait(stats.avgFirstResponseSeconds), `Tempo médio até a primeira resposta humana. ${window}`)}
      {tile("Atendimento", formatWait(stats.avgResponseSeconds), `Tempo médio de todas as respostas humanas (${formatCount(stats.responseSamples)} respostas). ${window}`)}
      {tile("Aguardando", formatCount(stats.awaitingNow), `Conversas em que o cliente falou por último. ${stats.unanswered} nunca receberam resposta humana.`, stats.awaitingNow > 0)}
    </div>
  );
}

/** Quem atende esta conversa + transferência. Sem `operators` (rota ausente) mostra só o selo, nunca um seletor falso. */
function OwnerControl({ conversation, operators, onAssign }: { conversation: PlatformConversation; operators?: ChatOperators; onAssign?: (conversationId: string, assigneeId: string | null) => void }): ReactElement {
  const owner = conversation.assignee ?? null;
  const mine = Boolean(owner && operators?.me && owner.id === operators.me.id);
  const candidates = (operators?.operators ?? []).filter((operator) => operator.id !== owner?.id);
  return (
    <div className="flex items-center gap-2" data-section="owner" data-owner-id={owner?.id ?? ""}>
      {owner ? (
        <Badge tone={mine ? "success" : "info"}><UserCheck className="h-3 w-3" aria-hidden focusable={false} />{mine ? "Você atende" : `Atendendo: ${firstName(owner.name)}`}</Badge>
      ) : (
        <Badge tone="neutral">Sem responsável</Badge>
      )}
      {onAssign && operators && (candidates.length > 0 || owner) ? (
        <select
          aria-label="Transferir conversa"
          value=""
          data-action="transfer"
          className="rounded-control border border-border bg-surface px-2 py-1 text-xs font-bold text-ink"
          onChange={(event) => {
            const value = event.target.value;
            if (value) onAssign(conversation.id, value === "__release" ? null : value);
          }}
        >
          <option value="">Transferir…</option>
          {candidates.map((operator: ChatOperator) => <option key={operator.id} value={operator.id}>{operator.name}</option>)}
          {owner ? <option value="__release">Liberar (sem responsável)</option> : null}
        </select>
      ) : null}
    </div>
  );
}

function GroupAvatar({ name }: { name: string }): ReactElement {
  return (
    <span className="relative inline-flex">
      <Avatar name={name} />
      <span className="absolute -bottom-1 -right-1 grid h-[18px] w-[18px] place-items-center rounded-pill border-2 border-surface bg-info text-white">
        <Users className="h-3 w-3" aria-hidden focusable={false} />
      </span>
    </span>
  );
}

/** Grupo aberto: mesma estrutura do chat individual, sinalizada como grupo. Sem histórico recebido. */
function GroupPane({ group, tags, composer }: { group: WhatsAppGroup; tags: TagMap; composer?: (group: WhatsAppGroup) => React.ReactNode }): ReactElement {
  return (
    <>
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3" data-group-id={group.id}>
        <Link to="/inbox" className="text-sm font-bold text-muted hover:text-ink lg:hidden">
          ← Conversas
        </Link>
        <GroupAvatar name={group.name} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-lg font-extrabold text-ink">{group.name}</span>
          <span className="truncate text-xs text-muted">{group.size !== null ? `Grupo do WhatsApp · ${formatCount(group.size)} participantes` : "Grupo do WhatsApp"}</span>
        </span>
        <Badge tone="info"><Users className="h-3 w-3" aria-hidden focusable={false} />Grupo</Badge>
        {group.simulated ? <Badge tone="warning">Simulador</Badge> : null}
        <div className="flex w-full flex-wrap items-center gap-1.5" data-section="tags">
          <TagChips tags={tags?.[group.id] ?? NO_TAGS} max={8} />
          <TagEditor id={group.id} tags={tags?.[group.id] ?? NO_TAGS} available={tags !== null} />
        </div>
      </header>
      <div className="grid min-h-0 flex-1 place-items-center bg-surface-soft p-8 text-center" data-status="awaiting_source" data-section="historico-grupo">
        <div className="flex max-w-sm flex-col items-center gap-2 text-muted">
          <Users className="h-10 w-10" aria-hidden focusable={false} />
          <p className="m-0 text-base font-extrabold text-ink">Histórico do grupo ainda não é recebido</p>
          <p className="m-0 text-sm">O WAPI da TDZAIN permite listar e responder a um grupo, mas ainda não entrega as mensagens que os participantes enviam. O que você escrever abaixo vai para este grupo.</p>
        </div>
      </div>
      <footer className="border-t border-border p-4">{composer ? composer(group) : <p className="m-0 text-sm text-muted">Resposta indisponível nesta visualização.</p>}</footer>
    </>
  );
}

function ChatEmpty(): ReactElement {
  return (
    <div className="grid flex-1 place-items-center p-8 text-center">
      <div className="flex max-w-xs flex-col items-center gap-3 text-muted">
        <MessagesSquare className="h-12 w-12" aria-hidden focusable={false} />
        <p className="m-0 text-lg font-extrabold text-ink">Selecione uma conversa</p>
        <p className="m-0 text-sm">Escolha um contato na lista para ver o histórico e responder.</p>
      </div>
    </div>
  );
}

function ChatPane({
  conversation,
  reference,
  composer,
  tags,
  onReact,
  onMarkRead,
  onTranscribe,
  onReply,
  aiMode,
  suggestion,
  operators,
  onAssign,
  pictureFor
}: {
  operators?: ChatOperators;
  onAssign?: (conversationId: string, assigneeId: string | null) => void;
  pictureFor: (conversation: PlatformConversation) => string | null;
  conversation: PlatformConversation;
  reference: Date;
  composer?: (conversation: PlatformConversation) => React.ReactNode;
  tags: TagMap;
  onReact?: (conversationId: string, messageId: string, emoji: string) => void;
  onMarkRead?: (conversationId: string) => void;
  onTranscribe?: (conversationId: string, messageId: string) => Promise<string>;
  onReply?: (message: ReplyTarget) => void;
  aiMode?: AiLiveMode;
  suggestion?: (conversation: PlatformConversation) => React.ReactNode;
}): ReactElement {
  const count = conversation.messages.length;
  const richChat = conversation.channel === "whatsapp" && conversation.kind !== undefined;
  const unread = conversation.unreadCount ?? 0;
  // Abre (e a cada mensagem nova) no fim da conversa, como toda caixa de entrada.
  useEffect(() => {
    const list = document.querySelector(".conversation-timeline");
    if (list) list.scrollTop = list.scrollHeight;
  }, [conversation.id, count]);
  // Abrir a conversa a marca como lida (visto azul no WhatsApp e contador zerado).
  useEffect(() => {
    if (richChat && unread > 0) onMarkRead?.(conversation.id);
  }, [conversation.id, unread, richChat]);
  const human = conversation.mode === "human";
  const [contactOpen, setContactOpen] = useState(false);
  const detail = toConversationDetail(conversation);
  return (
    <>
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3" data-conversation-id={conversation.id}>
        <Link to="/inbox" className="text-sm font-bold text-muted hover:text-ink lg:hidden">
          ← Conversas
        </Link>
        {/* O topo inteiro abre o painel do contato (como no Chatwoot): perfil, CRM, pedidos e ações. */}
        <button
          type="button"
          onClick={() => setContactOpen(true)}
          aria-haspopup="dialog"
          aria-label={`Ver detalhes do contato ${conversation.displayName || conversation.customerId}`}
          data-action="open-contact"
          className="group -mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-control px-2 py-1 text-left transition-colors hover:bg-surface-soft focus-visible:outline-none"
        >
          <ChatAvatar name={stripJid(conversation.displayName || conversation.customerId)} pictureUrl={pictureFor(conversation)} group={conversation.kind === "group"} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-lg font-extrabold text-ink">{stripJid(conversation.displayName || conversation.customerId)}</span>
            <span className="truncate text-xs text-muted">{conversation.kind === "group" ? `Grupo · ${conversation.group?.size ?? conversation.participants?.length ?? 0} participantes` : `${conversation.contact?.phoneDisplay ?? conversation.phone ?? "Sem telefone"} · ver detalhes do contato`}</span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" aria-hidden focusable={false} />
        </button>
        <Badge tone={human || !aiAnswers(aiMode, conversation) ? "warning" : "info"}>{human || !aiAnswers(aiMode, conversation) ? "Atendimento humano" : "Atendimento por IA"}</Badge>
        {conversation.kind !== "group" ? <OwnerControl conversation={conversation} operators={operators} onAssign={onAssign} /> : null}
        {suggestion ? suggestion(conversation) : null}
        <div className="flex w-full flex-wrap items-center gap-1.5" data-section="tags">
          <TagChips tags={tags?.[conversation.id] ?? NO_TAGS} max={8} />
          <TagEditor id={conversation.id} tags={tags?.[conversation.id] ?? NO_TAGS} available={tags !== null} />
        </div>
      </header>
      <ContactDrawer
        conversation={conversation}
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        onExport={detail ? () => downloadTextFile(conversationExportFilename(detail), conversationToText(detail)) : undefined}
      />
      <ChatTimeline
        messages={conversation.messages as never}
        conversationName={stripJid(conversation.displayName || "Cliente")}
        now={reference}
        onReact={richChat && onReact ? (messageId, emoji) => onReact(conversation.id, messageId, emoji) : undefined}
        onTranscribe={richChat && onTranscribe ? (messageId) => onTranscribe(conversation.id, messageId) : undefined}
        onReply={conversation.kind === "group" && onReply ? (target) => onReply({ ...target, conversationId: conversation.id }) : undefined}
      />
      <footer className="border-t border-border p-4">{composer ? composer(conversation) : <p className="m-0 text-sm text-muted">Resposta indisponível nesta visualização.</p>}</footer>
    </>
  );
}

function ContactPane({ conversation, reference, copilot, aiMode, pictureFor }: { pictureFor: (conversation: PlatformConversation) => string | null; conversation: PlatformConversation; reference: Date; copilot?: (conversation: PlatformConversation) => React.ReactNode; aiMode?: AiLiveMode }): ReactElement {
  const waiting = isWaiting(conversation);
  const sla = waiting ? classifySlaStatus(conversation, reference) : null;
  const minutes = waiting ? slaWaitingMinutes(conversation, reference) : null;
  const row = (label: string, value: React.ReactNode) => (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <dt className="text-muted">{label}</dt>
      <dd className="m-0 text-right font-bold text-ink">{value}</dd>
    </div>
  );
  return (
    <aside aria-label="Dados do contato" className="hidden min-h-0 flex-col gap-5 overflow-y-auto border-l border-border p-5 2xl:flex">
      <div className="flex flex-col items-center gap-2 text-center">
        <ChatAvatar name={stripJid(conversation.displayName || conversation.customerId)} pictureUrl={pictureFor(conversation)} size="lg" />
        <h3 className="text-lg font-extrabold text-ink">{stripJid(conversation.displayName || conversation.customerId)}</h3>
        <p className="m-0 text-sm text-muted">{conversation.contact?.phoneDisplay ?? conversation.phone ?? "Sem telefone"}</p>
        {conversation.contact && !conversation.contact.hasName ? <Badge tone="neutral">sem nome público no WhatsApp</Badge> : null}
        <Link to={`/clientes/${conversation.customerId}`} className={buttonClass("outline", "sm")}>
          Abrir no CRM
        </Link>
      </div>
      <dl className="m-0">
        {row("Canal", conversation.channel === "whatsapp" ? "WhatsApp" : "Chat da loja")}
        {row("Atendimento", conversation.mode !== "human" && aiAnswers(aiMode, conversation) ? "IA" : "Humano")}
        {row("Lead score", formatCount(conversation.leadScore))}
        {row("Mensagens", formatCount(conversation.messages.length))}
      </dl>
      {sla ? (
        <p
          className={"m-0 rounded-control px-3 py-2 text-sm font-bold " + (sla === "breached" ? "bg-danger-soft text-danger" : "bg-warning-soft text-ink")}
          data-sla-status={sla}
        >
          {minutes === null ? "Aguardando resposta humana." : `Aguardando há ${formatCount(minutes)} min — ${sla === "breached" ? "fora do SLA" : "dentro do SLA"}.`}
        </p>
      ) : null}
      {copilot ? copilot(conversation) : null}
    </aside>
  );
}

/** Lê o arquivo como base64 puro (sem o prefixo `data:`), o formato que o `sendMedia` espera. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo anexado."));
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}

/** Mensagem do grupo escolhida para ser respondida (citada na resposta). */
export interface ReplyTarget { conversationId: string; messageId: string; authorName: string; text: string }

/** Sugestão da IA que o operador mandou aplicar na caixa de resposta (`nonce` distingue aplicar o mesmo texto de novo). */
export interface AppliedSuggestion { conversationId: string; text: string; nonce: number }

/** Caixa de resposta do chat: assumir/devolver e responder pelo WAPI da TDZAIN (só WhatsApp). */
export function ChatComposer({ conversation, aiMode, appliedSuggestion, replyTo, onClearReply }: { conversation: PlatformConversation; aiMode?: AiLiveMode; appliedSuggestion?: AppliedSuggestion | null; replyTo?: ReplyTarget | null; onClearReply?: () => void }): ReactElement {
  const { send, sendMedia, setMode, assign } = useChannelConversationActions(conversation.id);
  const quickReplies = useQuickReplies();
  const operators = useChatOperators();
  const [text, setText] = useState("");
  // Convite "atribuir a você": uma recusa vale só para esta conversa (e só até trocar de conversa).
  const [declinedFor, setDeclinedFor] = useState<string | null>(null);
  // "Aplicar sugestão da IA": copia o texto para a caixa (editável). Quem envia é sempre o operador.
  const appliedNonce = appliedSuggestion?.nonce;
  useEffect(() => {
    if (appliedSuggestion) setText(appliedSuggestion.text);
  }, [appliedNonce]);
  if (conversation.channel !== "whatsapp") {
    return <p className="m-0 text-sm text-muted">Esta conversa é do chat da loja: só o WhatsApp tem resposta humana por aqui.</p>;
  }
  // Só o piloto responde sozinho, então só nele é preciso "assumir" antes de escrever.
  // Grupo nunca é respondido pela IA: a equipe escreve direto, sem "assumir".
  const aiWouldAnswer = conversation.kind !== "group" && conversation.mode !== "human" && aiAnswers(aiMode, conversation);
  const human = !aiWouldAnswer;
  const error = send.error ?? sendMedia.error ?? setMode.error ?? assign.error;
  if (!human) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3" data-atendimento-panel data-mode={conversation.mode}>
        <p className="m-0 text-sm text-muted">A IA está respondendo. Assuma o atendimento para escrever ao cliente.</p>
        <Button variant="primary" disabled={setMode.isPending} data-action="takeover" onClick={() => setMode.mutate("takeover")}>
          Assumir atendimento
        </Button>
        {error ? <p className="m-0 w-full text-sm text-danger" role="alert">{`Não foi possível concluir: ${error.message}`}</p> : null}
      </div>
    );
  }
  // Só vale se a citação é desta conversa (trocar de conversa não pode citar mensagem de outra).
  const activeReply = replyTo?.conversationId === conversation.id ? replyTo : null;
  const quoteOf = (value: string) => (activeReply ? { text: value, quotedMessageId: activeReply.messageId } : value);
  const me = operators.data?.me ?? null;
  const offerClaim = Boolean(me) && conversation.kind !== "group" && !conversation.assigneeId && text.trim().length > 0 && declinedFor !== conversation.id;
  return (
    <div data-atendimento-panel data-mode={conversation.mode}>
      {offerClaim && me ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-control border border-border bg-brand-wash px-3 py-2 text-sm" role="status" data-claim-invite={conversation.id}>
          <UserCheck className="h-4 w-4 shrink-0 text-brand-text" aria-hidden focusable={false} />
          <span className="min-w-0 flex-1 font-bold text-ink">Esta conversa não tem responsável. Deseja atribuir a você mesmo?</span>
          <Button size="sm" variant="primary" disabled={assign.isPending} data-action="claim" onClick={() => assign.mutate(me.id)}>Atribuir a mim</Button>
          <Button size="sm" variant="outline" data-action="claim-dismiss" onClick={() => setDeclinedFor(conversation.id)}>Agora não</Button>
        </div>
      ) : null}
      {activeReply ? (
        <div className="mb-2 flex items-start gap-2 border-l-[3px] border-brand bg-surface-soft px-3 py-1.5 text-xs" data-reply-to={activeReply.messageId}>
          <div className="min-w-0 flex-1">
            <b className="block text-brand-text">Respondendo a {activeReply.authorName}</b>
            <span className="line-clamp-2 text-muted">{activeReply.text}</span>
          </div>
          <button type="button" onClick={onClearReply} aria-label="Cancelar resposta" className="shrink-0 rounded-pill px-2 text-base font-bold text-muted hover:text-ink">×</button>
        </div>
      ) : null}
      <ChatCockpit
        value={text}
        onChange={setText}
        sending={send.isPending || sendMedia.isPending}
        error={error ? `Não foi possível concluir: ${error.message}` : null}
        textareaLabel="Resposta pelo WAPI da TDZAIN"
        onSendWithAttachments={async (attachments) => {
          // O texto digitado vira legenda do primeiro anexo que aceita legenda (foto, vídeo,
          // documento); áudio e figurinha não têm legenda no WhatsApp, então o texto sobra e vai
          // como mensagem à parte, depois das mídias.
          let caption = text.trim();
          for (const item of attachments) {
            const mimeType = item.file.type || "application/octet-stream";
            const kind = item.kind === "image" && mimeType.startsWith("video/") ? "video" : item.kind;
            const captionable = kind === "image" || kind === "video" || kind === "document";
            await sendMedia.mutateAsync({
              kind,
              fileName: item.name,
              mimeType,
              dataBase64: await fileToBase64(item.file),
              ...(captionable && caption ? { caption } : {})
            });
            if (captionable) caption = "";
          }
          if (caption) await send.mutateAsync(quoteOf(caption));
          setText("");
          onClearReply?.();
        }}
        placeholder="Escreva sua resposta…"
        onSend={() => {
          const value = text.trim();
          if (!value) return;
          send.mutate(quoteOf(value), { onSuccess: () => { setText(""); onClearReply?.(); } });
        }}
        sideActions={
          <>
            {quickReplies.data?.length ? (
              <select
                aria-label="Inserir resposta rápida"
                className={controlClass + " w-[14rem]"}
                value=""
                onChange={(event) => {
                  const reply = quickReplies.data?.find((item) => item.id === event.target.value);
                  if (reply) setText((current) => (current ? `${current}\n${reply.body}` : reply.body));
                }}
              >
                <option value="">Respostas rápidas…</option>
                {quickReplies.data.map((reply) => (
                  <option key={reply.id} value={reply.id}>{`/${reply.shortcut} · ${reply.title}`}</option>
                ))}
              </select>
            ) : null}
            {aiMode === undefined || aiMode === "pilot" ? (
              <Button disabled={setMode.isPending} data-action="resume" onClick={() => setMode.mutate("resume")}>
                Devolver para a IA
              </Button>
            ) : null}
          </>
        }
      />
      {send.data?.dispatch === "queued" ? (
        <p className="m-0 mt-2 text-sm text-muted" data-dispatch="queued">Fora do horário comercial: mensagem enfileirada, será enviada na abertura.</p>
      ) : null}
    </div>
  );
}

/** Caixa de resposta do grupo: mesmo cockpit, envio de texto ao grupo (único destino por vez). */
export function GroupComposer({ group }: { group: WhatsAppGroup }): ReactElement {
  const send = useWhatsAppGroupSend();
  const [text, setText] = useState("");
  return (
    <div data-atendimento-panel data-kind="group">
      <ChatCockpit
        value={text}
        onChange={setText}
        sending={send.isPending}
        error={send.error ? `Não foi possível enviar: ${send.error.message}` : null}
        textareaId="group-text"
        textareaLabel={`Mensagem para o grupo ${group.name}`}
        placeholder={`Responder ao grupo ${group.name}…`}
        onSend={() => {
          const value = text.trim();
          if (!value) return;
          send.mutate({ groupId: group.id, text: value }, { onSuccess: () => setText("") });
        }}
      />
      {send.isSuccess ? <p className="m-0 mt-2 text-sm font-bold text-success" role="status">Mensagem enviada ao grupo.</p> : null}
    </div>
  );
}

function AtendimentoLive({ dataset, selectedId, selectedGroupId }: { dataset: DashboardDataset; selectedId: string | null; selectedGroupId: string | null }): ReactElement {
  // Só consulta a conversa ao vivo quando há uma aberta (o hook exige um id).
  const live = useChannelConversation(selectedId ?? "");
  const groups = useWhatsAppGroups();
  const tags = useConversationTags();
  const chat = useChatActions(selectedId ?? "");
  const aiStatus = useAiStatus();
  // Carregando ou servidor de IA indisponível = tratada como desligada (atendimento humano).
  const aiMode = aiLiveMode(aiStatus.data);
  // Lista com polling curto (5 s): é dela que saem os alertas de mensagem nova e os selos de espera.
  // Sem resposta (rota fora do ar) vale o dataset do dashboard, que atualiza a cada 30 s.
  const pulse = useInboxPulse();
  const stats = useInboxStats();
  const operators = useChatOperators();
  const actions = useChannelConversationActions(selectedId ?? "");
  // Triagem por IA: só as conversas que esperam a equipe (até 20, as de maior placar). A nota vale até a
  // próxima triagem; erro (sem chave, teto diário) mostra o motivo e a lista segue com a regra fixa.
  const triageRun = useInboxTriage();
  const [triageNotes, setTriageNotes] = useState<ReadonlyMap<string, TriageNote>>(new Map());
  const conversations = pulse.data ?? dataset.conversations;
  // Foto sincronizada: guarda a última foto vista de cada contato (da lista OU da conversa aberta),
  // então a miniatura da lista acompanha a do histórico mesmo quando a URL expira numa das duas leituras.
  const pictures = useRef(new Map<string, string>());
  const pictureFor = (conversation: PlatformConversation): string | null => {
    const url = conversation.contact?.pictureUrl ?? conversation.group?.pictureUrl ?? null;
    if (url) pictures.current.set(conversation.id, url);
    return url ?? pictures.current.get(conversation.id) ?? null;
  };
  if (live.data) pictureFor(live.data);
  const [applied, setApplied] = useState<AppliedSuggestion | null>(null);
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  return (
    <AtendimentoView
      dataset={{ ...dataset, conversations }}
      selectedId={selectedId}
      selectedGroupId={selectedGroupId}
      operators={operators.data}
      onAssign={(_conversationId, assigneeId) => actions.assign.mutate(assigneeId)}
      stats={stats.data ?? null}
      pictureFor={pictureFor}
      triage={triageNotes}
      triaging={triageRun.isPending}
      triageError={triageRun.error ? "A IA não conseguiu priorizar agora; a ordem segue a regra de atenção." : null}
      onTriage={() => {
        const now = new Date();
        const candidates = conversations
          .map((conversation) => ({ conversation, attention: attentionOf(conversation, now, aiAnswers(aiMode, conversation)) }))
          .filter(({ attention }) => attention.level !== "none")
          .sort((a, b) => b.attention.score - a.attention.score)
          .slice(0, 20)
          .map(({ conversation, attention }) => ({ id: conversation.id, name: stripJid(conversation.displayName || conversation.customerId), waitingMinutes: attention.waitingMinutes ?? 0, unassigned: !conversation.assigneeId }));
        if (!candidates.length) return;
        triageRun.mutate(candidates, { onSuccess: (items) => setTriageNotes(new Map(items.map((item) => [item.id, { urgency: item.urgency, reason: item.reason }]))) });
      }}
      alerts={<InboxAlerts conversations={pulse.data} openId={selectedId} pictureFor={pictureFor} />}
      liveConversation={selectedId ? live.data : undefined}
      aiMode={aiMode}
      composer={(conversation) => <ChatComposer conversation={conversation} aiMode={aiMode} appliedSuggestion={applied?.conversationId === conversation.id ? applied : null} replyTo={replyTo} onClearReply={() => setReplyTo(null)} />}
      suggestion={(conversation) => <AiSuggestionDrawer conversation={conversation} aiMode={aiMode} onApply={(text) => setApplied({ conversationId: conversation.id, text, nonce: Date.now() })} />}
      groups={groups.data}
      groupComposer={(group) => <GroupComposer group={group} />}
      tags={tags.data ?? null}
      onReact={(_conversationId, messageId, emoji) => chat.react.mutate({ messageId, emoji })}
      onMarkRead={() => { if (!chat.markRead.isPending) chat.markRead.mutate(); }}
      onTranscribe={(_conversationId, messageId) => chat.transcribe.mutateAsync({ messageId })}
      onReply={setReplyTo}
      copilot={(conversation) => <CopilotPanel conversation={conversation} />}
    />
  );
}

/** Rota de `/inbox`, `/inbox/:conversationId` e `/inbox/grupo/:groupId`. Com `?kpi=` mantém o drill-down de indicador. */
export function Atendimento(): ReactElement {
  const { conversationId, groupId } = useParams<{ conversationId?: string; groupId?: string }>();
  const [searchParams] = useSearchParams();
  if (searchParams.get("kpi")) return <Inbox />;
  return (
    <DatasetBoundary>
      {(dataset: DashboardDataset) => <AtendimentoLive dataset={dataset} selectedId={conversationId ?? null} selectedGroupId={groupId ?? null} />}
    </DatasetBoundary>
  );
}
